import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ChevronLeft, Copy, Check } from 'lucide-react'
import clsx from 'clsx'
import { API_BASE_URL } from '../../../config/api'

function CodeBlock({ code, language = 'bash' }: { code: string; language?: string }) {
  const [copied, setCopied] = useState(false)

  function copy() {
    navigator.clipboard.writeText(code)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <div className="relative group">
      <pre className="bg-gray-900 text-gray-100 rounded-xl p-4 text-sm overflow-x-auto leading-relaxed">
        <code>{code}</code>
      </pre>
      <button onClick={copy} className="absolute top-3 right-3 h-8 w-8 flex items-center justify-center rounded-lg bg-white/10 text-gray-400 hover:text-white hover:bg-white/20 transition-colors opacity-0 group-hover:opacity-100">
        {copied ? <Check className="w-4 h-4 text-success-400" /> : <Copy className="w-4 h-4" />}
      </button>
    </div>
  )
}

function ParamTable({ params }: { params: { name: string; type: string; required: boolean; desc: string }[] }) {
  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden">
      <table className="w-full">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase">Campo</th>
            <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase">Tipo</th>
            <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase">Obrigatório</th>
            <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase">Descrição</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {params.map(p => (
            <tr key={p.name}>
              <td className="px-4 py-2.5"><code className="text-sm text-brand-600 bg-brand-50 px-1.5 py-0.5 rounded">{p.name}</code></td>
              <td className="px-4 py-2.5 text-sm text-gray-500">{p.type}</td>
              <td className="px-4 py-2.5">
                {p.required
                  ? <span className="text-xs font-medium text-error-500 bg-error-50 px-2 py-0.5 rounded-full">Sim</span>
                  : <span className="text-xs text-gray-400">Não</span>
                }
              </td>
              <td className="px-4 py-2.5 text-sm text-gray-600">{p.desc}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

const tabs = [
  { id: 'quick', label: 'Início rápido' },
  { id: 'api', label: 'API de captação' },
  { id: 'geo', label: 'Dados geográficos' },
  { id: 'form', label: 'Formulário público' },
  { id: 'embed', label: 'Embed (iframe)' },
  { id: 'errors', label: 'Erros' },
]

export default function ApiDocsPage() {
  const [activeTab, setActiveTab] = useState('quick')
  const navigate = useNavigate()
  const baseUrl = API_BASE_URL

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button onClick={() => navigate('/settings/integrations')} className="h-9 w-9 flex items-center justify-center rounded-lg hover:bg-gray-200/60 text-gray-400 hover:text-gray-600 transition-colors">
          <ChevronLeft className="w-5 h-5" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Documentação da API</h1>
          <p className="text-gray-500 mt-1">Integre o Sispoli com seu site, WhatsApp e outros canais</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 rounded-lg p-1 overflow-x-auto">
        {tabs.map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={clsx('px-4 py-2 rounded-md text-sm font-medium whitespace-nowrap transition-colors', activeTab === tab.id ? 'bg-white text-gray-900 shadow-xs' : 'text-gray-500 hover:text-gray-700')}>
            {tab.label}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="bg-white rounded-2xl border border-gray-200 p-6 xl:p-8">

        {/* Início rápido */}
        {activeTab === 'quick' && (
          <div className="space-y-8 max-w-3xl">
            <div>
              <h2 className="text-xl font-bold text-gray-900 mb-2">Início rápido</h2>
              <p className="text-gray-600 leading-relaxed">
                A API do Sispoli permite captar eleitores automaticamente a partir do seu site, landing pages, bots de WhatsApp ou qualquer sistema externo.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-3">3 formas de integrar</h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="border border-gray-200 rounded-xl p-4">
                  <p className="text-sm font-semibold text-gray-800 mb-1">1. Link público</p>
                  <p className="text-xs text-gray-500">Compartilhe o link do formulário no WhatsApp, Instagram, etc. Nenhum código necessário.</p>
                </div>
                <div className="border border-gray-200 rounded-xl p-4">
                  <p className="text-sm font-semibold text-gray-800 mb-1">2. Embed (iframe)</p>
                  <p className="text-xs text-gray-500">Cole o código HTML no seu site. O formulário aparece dentro da página.</p>
                </div>
                <div className="border border-gray-200 rounded-xl p-4">
                  <p className="text-sm font-semibold text-gray-800 mb-1">3. API direta</p>
                  <p className="text-xs text-gray-500">Crie seu próprio formulário e envie os dados via API. Máxima flexibilidade.</p>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-3">Passo a passo</h3>
              <div className="space-y-4">
                <div className="flex gap-4">
                  <div className="w-8 h-8 bg-brand-50 rounded-lg flex items-center justify-center flex-shrink-0">
                    <span className="text-sm font-bold text-brand-600">1</span>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-800">Crie uma API Key</p>
                    <p className="text-sm text-gray-500">Vá em Integrações → Nova Chave. A chave será usada para autenticar as chamadas.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="w-8 h-8 bg-brand-50 rounded-lg flex items-center justify-center flex-shrink-0">
                    <span className="text-sm font-bold text-brand-600">2</span>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-800">Crie um Formulário de captação</p>
                    <p className="text-sm text-gray-500">Defina quais campos são obrigatórios e em quais canais será usado.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="w-8 h-8 bg-brand-50 rounded-lg flex items-center justify-center flex-shrink-0">
                    <span className="text-sm font-bold text-brand-600">3</span>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-800">Integre</p>
                    <p className="text-sm text-gray-500">Use o link público, embed ou API direta para começar a captar eleitores.</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-brand-25 border border-brand-100 rounded-xl p-4">
              <p className="text-sm font-medium text-brand-800 mb-1">URL base da API</p>
              <code className="text-sm text-brand-600">{baseUrl}/v1</code>
            </div>
          </div>
        )}

        {/* API de captação */}
        {activeTab === 'api' && (
          <div className="space-y-8 max-w-3xl">
            <div>
              <h2 className="text-xl font-bold text-gray-900 mb-2">API de captação</h2>
              <p className="text-gray-600">Envie dados do seu próprio formulário diretamente para o Sispoli.</p>
            </div>

            {/* Endpoint */}
            <div>
              <div className="flex items-center gap-2 mb-4">
                <span className="text-xs font-bold text-white bg-success-500 px-2 py-1 rounded">POST</span>
                <code className="text-sm text-gray-800">{baseUrl}/v1/public/capture</code>
              </div>

              <h4 className="text-sm font-semibold text-gray-700 mb-2">Headers</h4>
              <CodeBlock code={`X-API-Key: sp_live_sua_chave_aqui
Content-Type: application/json`} />
            </div>

            <div>
              <h4 className="text-sm font-semibold text-gray-700 mb-2">Parâmetros</h4>
              <ParamTable params={[
                { name: 'name', type: 'string', required: true, desc: 'Nome completo do eleitor' },
                { name: 'phone', type: 'string', required: true, desc: 'Telefone celular — apenas números, 11 dígitos (DDD + 9 + número). Ex: 82999990000' },
                { name: 'email', type: 'string', required: false, desc: 'Email' },
                { name: 'cpf', type: 'string', required: false, desc: 'CPF (só números, 11 dígitos)' },
                { name: 'nickname', type: 'string', required: false, desc: 'Apelido' },
                { name: 'birth_date', type: 'date', required: false, desc: 'Data de nascimento (YYYY-MM-DD)' },
                { name: 'gender', type: 'string', required: false, desc: 'M, F ou O' },
                { name: 'channel', type: 'string', required: false, desc: 'Canal: SITE, WHATSAPP, INSTAGRAM, etc.' },
                { name: 'geo_city_id', type: 'integer', required: false, desc: 'ID da cidade (ver aba Dados geográficos)' },
                { name: 'neighborhood', type: 'string', required: false, desc: 'Bairro' },
                { name: 'zip_code', type: 'string', required: false, desc: 'CEP (8 dígitos)' },
                { name: 'street', type: 'string', required: false, desc: 'Rua' },
                { name: 'number', type: 'string', required: false, desc: 'Número' },
                { name: 'form_id', type: 'integer', required: false, desc: 'ID do formulário de captação' },
                { name: 'form_slug', type: 'string', required: false, desc: 'Slug do formulário (alternativa ao form_id)' },
              ]} />
            </div>

            <div>
              <h4 className="text-sm font-semibold text-gray-700 mb-2">Exemplo com cURL</h4>
              <CodeBlock code={`curl -X POST ${baseUrl}/v1/public/capture \\
  -H "X-API-Key: sp_live_sua_chave_aqui" \\
  -H "Content-Type: application/json" \\
  -d '{
    "name": "João Silva",
    "phone": "82999999999",
    "email": "joao@email.com",
    "channel": "SITE"
  }'`} />
            </div>

            <div>
              <h4 className="text-sm font-semibold text-gray-700 mb-2">Exemplo com JavaScript (fetch)</h4>
              <CodeBlock language="javascript" code={`const response = await fetch('${baseUrl}/v1/public/capture', {
  method: 'POST',
  headers: {
    'X-API-Key': 'sp_live_sua_chave_aqui',
    'Content-Type': 'application/json',
  },
  body: JSON.stringify({
    name: 'João Silva',
    phone: '82999999999',
    email: 'joao@email.com',
    channel: 'SITE',
  }),
});

const data = await response.json();
// { data: { voter_id: 123 }, message: "Contato registrado com sucesso" }`} />
            </div>

            <div>
              <h4 className="text-sm font-semibold text-gray-700 mb-2">Exemplo com PHP</h4>
              <CodeBlock language="php" code={`$ch = curl_init('${baseUrl}/v1/public/capture');
curl_setopt($ch, CURLOPT_POST, true);
curl_setopt($ch, CURLOPT_HTTPHEADER, [
    'X-API-Key: sp_live_sua_chave_aqui',
    'Content-Type: application/json',
]);
curl_setopt($ch, CURLOPT_POSTFIELDS, json_encode([
    'name'  => 'João Silva',
    'phone' => '82999999999',
    'channel' => 'SITE',
]));
curl_setopt($ch, CURLOPT_RETURNTRANSFER, true);
$response = curl_exec($ch);`} />
            </div>

            <div>
              <h4 className="text-sm font-semibold text-gray-700 mb-2">Resposta de sucesso (201)</h4>
              <CodeBlock code={`{
  "data": {
    "voter_id": 123
  },
  "message": "Contato registrado com sucesso"
}`} />
            </div>
          </div>
        )}

        {/* Dados geográficos */}
        {activeTab === 'geo' && (
          <div className="space-y-8 max-w-3xl">
            <div>
              <h2 className="text-xl font-bold text-gray-900 mb-2">Dados geográficos</h2>
              <p className="text-gray-600">Consulte estados e cidades para popular selects nos seus formulários de captação.</p>
            </div>

            <div className="bg-brand-25 border border-brand-100 rounded-xl p-4">
              <p className="text-sm text-brand-800">
                <strong>Abrangência automática:</strong> Se o político for estadual ou municipal, o endpoint de estados retorna apenas o estado dele. Assim o formulário já vem filtrado sem o eleitor precisar selecionar.
              </p>
            </div>

            {/* Listar estados */}
            <div>
              <div className="flex items-center gap-2 mb-4">
                <span className="text-xs font-bold text-white bg-brand-500 px-2 py-1 rounded">GET</span>
                <code className="text-sm text-gray-800">{baseUrl}/v1/public/geo/states</code>
              </div>

              <h4 className="text-sm font-semibold text-gray-700 mb-2">Headers</h4>
              <CodeBlock code={`X-API-Key: sp_live_sua_chave_aqui`} />

              <h4 className="text-sm font-semibold text-gray-700 mb-2 mt-4">Resposta</h4>
              <CodeBlock code={`{
  "data": [
    { "id": 2, "code": "AL", "name": "Alagoas" }
  ],
  "scope": "STATE"
}

// scope pode ser: "NATIONAL", "STATE" ou "MUNICIPAL"
// Se STATE ou MUNICIPAL, retorna apenas o estado do político
// Se NATIONAL, retorna todos os 27 estados`} />
            </div>

            {/* Listar cidades */}
            <div>
              <div className="flex items-center gap-2 mb-4">
                <span className="text-xs font-bold text-white bg-brand-500 px-2 py-1 rounded">GET</span>
                <code className="text-sm text-gray-800">{`${baseUrl}/v1/public/geo/states/{stateId}/cities`}</code>
              </div>

              <h4 className="text-sm font-semibold text-gray-700 mb-2">Headers</h4>
              <CodeBlock code={`X-API-Key: sp_live_sua_chave_aqui`} />

              <h4 className="text-sm font-semibold text-gray-700 mb-2 mt-4">Resposta</h4>
              <CodeBlock code={`{
  "data": [
    { "id": 49, "name": "Água Branca", "ibge_id": 2700102 },
    { "id": 211, "name": "Anadia", "ibge_id": 2700201 },
    ...
  ],
  "state": { "id": 2, "code": "AL", "name": "Alagoas" }
}`} />
            </div>

            {/* Consulta CEP */}
            <div>
              <div className="flex items-center gap-2 mb-4">
                <span className="text-xs font-bold text-white bg-brand-500 px-2 py-1 rounded">GET</span>
                <code className="text-sm text-gray-800">{`${baseUrl}/v1/public/geo/cep/{cep}`}</code>
              </div>

              <h4 className="text-sm font-semibold text-gray-700 mb-2">Headers</h4>
              <CodeBlock code={`X-API-Key: sp_live_sua_chave_aqui`} />

              <h4 className="text-sm font-semibold text-gray-700 mb-2 mt-4">Resposta</h4>
              <CodeBlock code={`GET ${baseUrl}/v1/public/geo/cep/57036410

{
  "data": {
    "zip_code": "57036410",
    "street": "Rua Empresário Antônio Magalhães",
    "neighborhood": "Jatiúca",
    "city_name": "Maceió",
    "state_code": "AL",
    "state_name": "Alagoas",
    "geo_city_id": 2839,
    "geo_neighborhood_id": 1,
    "state_id": 2
  }
}`} />
              <p className="text-xs text-gray-500 mt-2">Use os IDs retornados (<code className="text-brand-600">geo_city_id</code>, <code className="text-brand-600">geo_neighborhood_id</code>) ao enviar a captação. O bairro é criado automaticamente se não existir.</p>
            </div>

            {/* Exemplo completo */}
            <div>
              <h4 className="text-sm font-semibold text-gray-700 mb-2">Exemplo completo com JavaScript</h4>
              <CodeBlock language="javascript" code={`const API_KEY = 'sp_live_sua_chave_aqui';
const BASE = '${baseUrl}/v1/public/geo';

// 1. Buscar estados (filtrado pela abrangência do político)
const statesRes = await fetch(\`\${BASE}/states\`, {
  headers: { 'X-API-Key': API_KEY }
});
const { data: states, scope } = await statesRes.json();

// Se scope for STATE ou MUNICIPAL, já tem 1 estado
if (scope !== 'NATIONAL' && states.length === 1) {
  const citiesRes = await fetch(
    \`\${BASE}/states/\${states[0].id}/cities\`,
    { headers: { 'X-API-Key': API_KEY } }
  );
  const { data: cities } = await citiesRes.json();
  // Popular select de cidades
}

// 2. Consultar CEP (auto-preencher endereço)
const cepRes = await fetch(\`\${BASE}/cep/57036410\`, {
  headers: { 'X-API-Key': API_KEY }
});
const { data: endereco } = await cepRes.json();
// endereco.street, endereco.neighborhood, endereco.geo_city_id...

// 3. Enviar captação com endereço completo
await fetch('${baseUrl}/v1/public/capture', {
  method: 'POST',
  headers: {
    'X-API-Key': API_KEY,
    'Content-Type': 'application/json',
  },
  body: JSON.stringify({
    name: 'João Silva',
    phone: '82999999999',
    geo_city_id: endereco.geo_city_id,
    neighborhood: endereco.neighborhood,
    street: endereco.street,
    zip_code: '57036410',
    number: '206',
    channel: 'SITE',
  }),
});`} />
            </div>

            <div>
              <h4 className="text-sm font-semibold text-gray-700 mb-2">Parâmetros de endereço na captação</h4>
              <ParamTable params={[
                { name: 'geo_city_id', type: 'integer', required: false, desc: 'ID da cidade (retornado pela API de cidades)' },
                { name: 'neighborhood', type: 'string', required: false, desc: 'Nome do bairro' },
                { name: 'zip_code', type: 'string', required: false, desc: 'CEP (8 dígitos, sem traço)' },
                { name: 'street', type: 'string', required: false, desc: 'Rua/logradouro' },
                { name: 'number', type: 'string', required: false, desc: 'Número' },
                { name: 'complement', type: 'string', required: false, desc: 'Complemento' },
              ]} />
            </div>
          </div>
        )}

        {/* Formulário público */}
        {activeTab === 'form' && (
          <div className="space-y-8 max-w-3xl">
            <div>
              <h2 className="text-xl font-bold text-gray-900 mb-2">Formulário público</h2>
              <p className="text-gray-600">Cada formulário gera um link único que pode ser compartilhado em qualquer lugar.</p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-3">Como funciona</h3>
              <div className="space-y-3">
                <div className="flex gap-3">
                  <div className="w-6 h-6 bg-brand-50 rounded flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-xs font-bold text-brand-600">1</span>
                  </div>
                  <p className="text-sm text-gray-600">Crie um formulário em Integrações → Novo Formulário</p>
                </div>
                <div className="flex gap-3">
                  <div className="w-6 h-6 bg-brand-50 rounded flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-xs font-bold text-brand-600">2</span>
                  </div>
                  <p className="text-sm text-gray-600">Copie o link público gerado</p>
                </div>
                <div className="flex gap-3">
                  <div className="w-6 h-6 bg-brand-50 rounded flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-xs font-bold text-brand-600">3</span>
                  </div>
                  <p className="text-sm text-gray-600">Compartilhe no WhatsApp, Instagram, bio do TikTok, etc.</p>
                </div>
              </div>
            </div>

            <div>
              <h4 className="text-sm font-semibold text-gray-700 mb-2">URL do formulário</h4>
              <CodeBlock code={`${baseUrl}/v1/public/forms/{slug}`} />
              <p className="text-xs text-gray-500 mt-2">O slug é gerado automaticamente ao criar o formulário.</p>
            </div>

            <div>
              <h4 className="text-sm font-semibold text-gray-700 mb-2">Obter dados do formulário (GET)</h4>
              <CodeBlock code={`GET ${baseUrl}/v1/public/forms/{slug}

Resposta:
{
  "data": {
    "form": {
      "name": "Landing Page Campanha",
      "require_name": true,
      "require_phone": true,
      "require_email": false,
      ...
    },
    "organization": {
      "name": "Diogo Deputado",
      "political_name": "Diogo",
      "photo_url": null
    }
  }
}`} />
            </div>

            <div>
              <h4 className="text-sm font-semibold text-gray-700 mb-2">Enviar dados do formulário (POST)</h4>
              <CodeBlock code={`POST ${baseUrl}/v1/public/forms/{slug}/submit
Content-Type: application/json

{
  "name": "João Silva",
  "phone": "82999999999"
}

Resposta (201):
{ "message": "Cadastro realizado com sucesso!" }

Resposta duplicado (409):
{ "error": "Você já está cadastrado!" }`} />
            </div>
          </div>
        )}

        {/* Embed */}
        {activeTab === 'embed' && (
          <div className="space-y-8 max-w-3xl">
            <div>
              <h2 className="text-xl font-bold text-gray-900 mb-2">Embed (iframe)</h2>
              <p className="text-gray-600">Incorpore o formulário de captação diretamente no seu site.</p>
            </div>

            <div>
              <h4 className="text-sm font-semibold text-gray-700 mb-2">Código HTML</h4>
              <CodeBlock code={`<iframe
  src="${baseUrl}/f/{slug}"
  width="100%"
  height="500"
  frameborder="0"
  style="border: none; border-radius: 12px;"
></iframe>`} />
            </div>

            <div>
              <h4 className="text-sm font-semibold text-gray-700 mb-2">Com estilo responsivo</h4>
              <CodeBlock code={`<div style="max-width: 480px; margin: 0 auto;">
  <iframe
    src="${baseUrl}/f/{slug}"
    width="100%"
    height="600"
    frameborder="0"
    style="border: none; border-radius: 16px; box-shadow: 0 4px 24px rgba(0,0,0,0.08);"
  ></iframe>
</div>`} />
            </div>

            <div className="bg-warning-50 border border-warning-200 rounded-xl p-4">
              <p className="text-sm text-warning-800">
                <strong>Nota:</strong> A página do formulário público ainda está em desenvolvimento.
                Por enquanto, use a API direta para integrar com seu site.
              </p>
            </div>
          </div>
        )}

        {/* Erros */}
        {activeTab === 'errors' && (
          <div className="space-y-8 max-w-3xl">
            <div>
              <h2 className="text-xl font-bold text-gray-900 mb-2">Formato dos campos</h2>
              <p className="text-gray-600 mb-4">Regras de formatação para os campos mais comuns.</p>
            </div>

            <div className="border border-gray-200 rounded-xl overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase">Campo</th>
                    <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase">Formato</th>
                    <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase">Exemplo</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {[
                    { campo: 'phone', formato: 'Apenas números, 11 dígitos: DDD (2) + 9 + número (8). Sem máscara, sem +55.', exemplo: '82999990000' },
                    { campo: 'cpf', formato: 'Apenas números, 11 dígitos. Sem pontos ou traço.', exemplo: '12345678900' },
                    { campo: 'birth_date', formato: 'Data no formato ISO: YYYY-MM-DD', exemplo: '1990-05-15' },
                    { campo: 'gender', formato: 'Uma letra: M (masculino), F (feminino) ou O (outro)', exemplo: 'M' },
                    { campo: 'zip_code', formato: 'Apenas números, 8 dígitos. Sem traço.', exemplo: '57000000' },
                    { campo: 'sus_number', formato: 'Apenas números, até 15 dígitos.', exemplo: '898000000000000' },
                  ].map(r => (
                    <tr key={r.campo}>
                      <td className="px-4 py-2.5 text-sm font-mono text-brand-600">{r.campo}</td>
                      <td className="px-4 py-2.5 text-sm text-gray-700">{r.formato}</td>
                      <td className="px-4 py-2.5 text-sm font-mono text-gray-500">{r.exemplo}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
              <p className="text-sm text-amber-800"><strong>Telefone:</strong> O campo phone deve conter exatamente 11 dígitos numéricos. Não envie com máscara ((82) 99999-0000), código do país (+55) ou espaços. O formato correto é <code className="bg-amber-100 px-1 rounded">82999990000</code>.</p>
            </div>

            <div>
              <h2 className="text-xl font-bold text-gray-900 mb-2">Códigos de erro</h2>
              <p className="text-gray-600">Respostas de erro que a API pode retornar.</p>
            </div>

            <div className="border border-gray-200 rounded-xl overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase">Código</th>
                    <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase">Erro</th>
                    <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase">Descrição</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {[
                    { code: '401', error: 'API Key obrigatória', desc: 'Header X-API-Key não enviado' },
                    { code: '401', error: 'API Key inválida ou inativa', desc: 'Chave não existe ou está desativada' },
                    { code: '401', error: 'API Key expirada', desc: 'A chave ultrapassou a data de expiração' },
                    { code: '404', error: 'Formulário não encontrado', desc: 'Slug do formulário inválido ou inativo' },
                    { code: '409', error: 'Contato já cadastrado', desc: 'Telefone já existe na base (retorna voter_id)' },
                    { code: '422', error: 'Validação falhou', desc: 'Campos obrigatórios não enviados' },
                  ].map((err, i) => (
                    <tr key={i}>
                      <td className="px-4 py-2.5"><span className={clsx('text-xs font-bold px-2 py-0.5 rounded', err.code === '401' ? 'bg-error-50 text-error-600' : err.code === '409' ? 'bg-warning-50 text-warning-600' : 'bg-gray-100 text-gray-600')}>{err.code}</span></td>
                      <td className="px-4 py-2.5 text-sm font-medium text-gray-800">{err.error}</td>
                      <td className="px-4 py-2.5 text-sm text-gray-500">{err.desc}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div>
              <h4 className="text-sm font-semibold text-gray-700 mb-2">Formato de erro</h4>
              <CodeBlock code={`{
  "error": "Mensagem descritiva do erro"
}

// Ou com detalhes de validação:
{
  "message": "The phone field is required.",
  "errors": {
    "phone": ["The phone field is required."]
  }
}`} />
            </div>

            <div>
              <h4 className="text-sm font-semibold text-gray-700 mb-2">Canais disponíveis</h4>
              <CodeBlock code={`MANUAL     - Cadastro manual
GABINETE   - Visita ao gabinete
LEADER     - Indicação de liderança
EVENT      - Evento
SITE       - Site
WHATSAPP   - WhatsApp
INSTAGRAM  - Instagram
FACEBOOK   - Facebook
TIKTOK     - TikTok
API        - Integração externa
OTHER      - Outro`} />
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
